import CommonCss from "nlib/common/common.module.scss";

import Css from "../../style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { TableCell, TableRow } from "nlib/ui/Table";
import {
  checkSelectedBusinessHasWhoAsk,
  checkSelectedBusinessRpaMode,
  getSelectedBusinessData
} from "selectors/businesses";
import { getActiveOrganization } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { getUserRole } from "selectors/user";
import { useSelector } from "react-redux";
import ActionCellContent from "nlib/common/ActionCellContent";
import Constants from "const/Constants";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import DescriptionCell from "./lib/DescriptionCell";
import DocumentIdCell from "nlib/pages/DocumentsPage/lib/DocumentsTable/lib/DocumentIdCell";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import IntegrationServices from "const/IntegrationServices";
import Money from "utils/Money";
import React, { useCallback, useMemo } from "react";
import TransactionsStatuses from "nlib/pages/TransactionsPage/TransactionsStatuses";
import TypeCell from "nlib/pages/DocumentsPage/lib/DocumentsTable/lib/TypeCell";
import UserRoles from "const/UserRoles";
import classNames from "classnames";
import moment from "moment";
import useShowCommonModal from "hooks/useShowCommonModal";

const DocumentsTableRow = ({ documentData, transactionAmount, onSelect }) => {
  const {
    id: documentId,
    paymentType,
    paid,
    status,
    amount,
    currency,
    issueDate,
    address = {}
  } = documentData || {};

  const { uiTexts, messages } = useSelector(getTextsData);

  const selectedBusinessData = useSelector(getSelectedBusinessData);

  const { businessOrganization } = useSelector(getActiveOrganization);

  const userRole = useSelector(getUserRole);

  const businessUser = UserRoles.checkIsBusiness(userRole);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const selectedBusinessHasWhoAsk = useSelector(checkSelectedBusinessHasWhoAsk);

  const { extraData: { integrationService } = {} } = selectedBusinessData;

  const { logo: serviceLogo } = IntegrationServices.getByValue(integrationService) || {};

  const showCommonModal = useShowCommonModal();

  const { labelLangId, iconComponent: Icon } = useMemo(() => {
    return TransactionsStatuses.getStatusData([
      status,
      businessOrganization,
      rpaMode && !selectedBusinessHasWhoAsk,
      businessUser
    ]);
  }, [businessOrganization, businessUser, rpaMode, selectedBusinessHasWhoAsk, status]);

  const amountContent = useMemo(() => {
    return Money.toMoneyString(amount, currency);
  }, [amount, currency]);

  const menuItems = useMemo(() => {
    return [{
      action: "pair",
      icon: Icons.Link,
      text: uiTexts.pair,
      theme: "primary"
    }];
  }, [uiTexts]);

  const handleActionClick = useCallback(async() => {
    if (amount !== transactionAmount) {
      const modalResult = await showCommonModal({
        text: messages.documentAmountMismatch,
        headerText: uiTexts.warning,
        confirm: true
      });

      if (!modalResult) return;
    }
    onSelect({ documentId });
  }, [
    amount,
    transactionAmount,
    onSelect,
    documentId,
    showCommonModal,
    messages.documentAmountMismatch,
    uiTexts.warning
  ]);

  const StatusIcon = status === DataStatuses.EXPORTED && serviceLogo
    ? () => <img src={serviceLogo} className={Css.serviceLogo} />
    : Icon;

  const statusTitle = status === DataStatuses.EXPORTED ? (paid ? uiTexts.paid : uiTexts.unpaid) : uiTexts[labelLangId];

  return (
    <TableRow
      className={Css.tableRow}>
      <TableCell className={classNames(Css.statusCell, Css[status])}>
        <div title={statusTitle} data-status={status}><StatusIcon /></div>
      </TableCell>
      <TableCell className={Css.typeCell}>
        <TypeCell documentData={documentData} />
      </TableCell>
      <TableCell className={Css.documentIdCell}>
        <DocumentIdCell documentData={documentData} />
      </TableCell>
      <TableCell className={Css.amountCell}>
        <div
          title={amountContent}
          className={classNames(Css.amount, (paymentType && (amount || amount === 0))
            ? CommonCss.coloredNumber
            : (paymentType ? "" : CommonCss.mediumDarkText))}
          data-negative={paymentType === DocumentPaymentTypes.BUY ? "" : undefined}>
          {amountContent}
        </div>
      </TableCell>
      <TableCell className={Css.addressCell}>
        {address && address.name
          ? <div title={address.name}>
            <Icons.User />
            <span>{address.name}</span>
          </div>
          : <div>{Constants.EMPTY_PLACEHOLDER}</div>}
      </TableCell>
      <TableCell className={Css.timestampCell}>
        <div>{issueDate ? moment.utc(issueDate).format(DateTimeFormats.DATE_TEXT) : Constants.EMPTY_PLACEHOLDER}</div>
      </TableCell>
      <TableCell className={Css.descriptionCell}>
        <DescriptionCell documentData={documentData} />
      </TableCell>
      <TableCell className={classNames(Css.actionsCell, Css.tableBodyCell)}>
        <ActionCellContent
          menuItems={menuItems}
          onClick={handleActionClick} />
      </TableCell>
    </TableRow>
  );
};

export default DocumentsTableRow;
